import styled from '@emotion/styled';
import React, { useState, useEffect } from 'react';
import { MINUTE_IN_MS } from '../lib/time';
import { computeOrderDuration, getOrderDate, Order } from '../models/order';
import OrderStatusBar, {
  COLORS,
  computeStatusDurationMinutes,
} from './OrderStatusBar';
import Overlay from './Overlay';
import VideoPlayer from 'react-video-player-extended';
import { ControlSelection } from 'react-video-player-extended';
import Clipboard from 'react-clipboard.js';
import moment from 'moment-timezone';

export interface OrderDetailsOverlayProps {
  order: null | Order;
  selectedStationId: null | String;
  videoTime: null | String;
  onClose?: () => any;
}

const getStationIndexByStationID = (id: String | null, order: Order | null) => {
  let requiredIndex = 0;

  if (order && order.stationVideos) {
    requiredIndex = order.stationVideos.findIndex(stationVideo => stationVideo.stationId === id);
  }

  return requiredIndex > -1 ? requiredIndex : 0;
}

const formatDate = (date: any) => date.format('ddd, MMM D, h:mm:ss A');

const OrderDetailsOverlay = ({ order, selectedStationId, videoTime, onClose = () => { } }: OrderDetailsOverlayProps) => {
  const [playing, setPlaying] = useState(false);
  const [stationIndex, setStationIndex] = useState(getStationIndexByStationID(selectedStationId, order));
  const [timeStart, setTimeStart] = useState(videoTime ? Number(videoTime) : 0);
  const [showTimeToOrderReset, setShowTimeToOrderReset] = useState(videoTime ? true : false);
  const [timeStartReset, setTimeStartReset] = useState(0);
  const [timeStartStore, setTimeStartStore] = useState(0);
  const [clipboardBgColor, setClipboardBgColor] = useState("transparent");
  const [clipboardSize, setClipboardSize] = useState(1);
  // [Station Index][Frame Index][0] Video time (ms)
  // [Station Index][Frame Index][1] Timestamp
  const [frameTimestamps, setFrameTimestamps] = useState<null | Array<number[][]>>(null);
  const orderDate = order ? getOrderDate(order) : null;

  // create state of displayedTimestamp with the initial value of orderDate.toLocaleString()
  const [displayedTimestamp, setDisplayedTimestamp] = useState(orderDate ? formatDate(orderDate) : '');

  async function fetchFrameTimestamps() {
    setFrameTimestamps(null);
    if (!order) return null;

    try {
      const frameResponses = await Promise.all(
        order.stationVideos.map(stationVideo => fetch(stationVideo.videoUrl + '.frames')));
      const frameMaps = await Promise.all(frameResponses.map(frameResponse => frameResponse.text()));
      const frameMapsIndexes = frameMaps.map(frameMap => frameMap.split('\n'));
      const frameTimestamps = new Array<number[][]>(frameMapsIndexes.length);

      frameMapsIndexes.forEach((frameMap, stationIndex) => {
        frameTimestamps[stationIndex] = [];
        frameMap.forEach((frame, frameIndex) => {
          const frameSplit = frame.split('->').map(time => +time);
          frameTimestamps[stationIndex][frameIndex] = [frameSplit[0], frameSplit[1]];
        });
      });
      setFrameTimestamps(frameTimestamps);

      return frameTimestamps;
    } catch (error) {
      console.log('Error fetching frame timestamps: ', error);
    }

    return null;
  }

  useEffect(() => {
    async function fetchFrameTimestampsAndLoadStart() {
      const fetchedTimestamps = await fetchFrameTimestamps();
      if (!fetchedTimestamps || !order) return;
  
      // A deep link (videoTime) stays at its own second. React 16 only got this
      // right by accident: it rendered setFrameTimestamps on its own, and the
      // player ignored the timeStart of 0 that followed. React 18 batches both.
      let toTimeSeconds = videoTime ? Number(videoTime) : 0;
      if (!videoTime && orderDate) {
        try {
          const toFrameIndex = binarySearch(fetchedTimestamps[stationIndex], 1,  orderDate.unix() * 1000, 0, fetchedTimestamps[stationIndex].length - 1);
          toTimeSeconds = fetchedTimestamps[stationIndex][toFrameIndex][0] / 1000;
          setDisplayedTimestamp(formatDate(orderDate));
        } catch (error) {
          console.log('Could not jump to order start: ', error);
        }
      }
      setTimeStart(toTimeSeconds);  
    }

    fetchFrameTimestampsAndLoadStart();
  }, [order]);

  if (!order) {
    return <Overlay show={false} />;
  }

  const handleClose = () => {
    onClose();
    setFrameTimestamps(null);
    setPlaying(false);
  };

  const markers = (() => {
    const { recognitionData } = order.stationVideos[stationIndex];
    if (!recognitionData) {
      return [];
    }

    const keyTimes = recognitionData.reduce((keyTimes, data) => {
      keyTimes.push(data.startTime);
      keyTimes.concat(data.keyframesTime || []);
      if (data.endTime) { keyTimes.push(data.endTime); }
      return keyTimes
    }, [] as string[])

    return keyTimes.map((keyTime, index) => ({
      id: index,
      time: keyTime.split(':').reduce((acc, time) => (60 * acc) + +time, 0),
      title: ''
    }))
  })();

  const controls: ControlSelection[] = ['Play', 'Time', 'Progress', 'Volume', 'FullScreen'];

  if (markers.length > 0) {
    controls.push('LastFrame');
    controls.push('NextFrame');
  }

  const handlePause = () => {
    setPlaying(false)
  }

  const handlePlay = () => {
    setPlaying(true)
  }

  let progressChange: number = 0;
  const handleProgress = async (e: any) => {
    // only add for debugging: console.log('Current time: ', e.target.currentTime);
    if (Math.abs(e.target.currentTime - progressChange) > .25) {
      // get real time from station and video and display in order-timestamp
      const stationTime = await getStationTime(stationIndex, e.target.currentTime);
      // only add for debugging: console.log('Station time: ', stationTime);
      if (stationTime !== null && orderDate !== null) {
        const stationTimeDate = moment(stationTime).utcOffset(order.location.timezoneOffset * 60);
        setDisplayedTimestamp(formatDate(stationTimeDate));

        // if stationTimeDate is more than 5 seconds different than orderDate, show reset button
        const timeDiff = Math.abs(stationTimeDate.diff(orderDate, 'milliseconds', true));
        setShowTimeToOrderReset(timeDiff > 3000);
      }

      progressChange = e.target.currentTime
      // only add for debugging: console.log('Update store time');
      setTimeStartStore(e.target.currentTime);
    }
  }

  const onResetTimeToOrder = () => {
    if (frameTimestamps === null || orderDate === null) return;

    const toFrameIndex = binarySearch(frameTimestamps[stationIndex], 1,  orderDate.unix() * 1000, 0, frameTimestamps[stationIndex].length - 1);
    const toTimeSeconds = frameTimestamps[stationIndex][toFrameIndex][0] / 1000;
    console.log('Reset time to order: ', toTimeSeconds);

    const videoElement = document.querySelector('.react-video-player') as HTMLVideoElement;
    if (videoElement) {
      videoElement.currentTime = toTimeSeconds;
    }
  }

  const handleDuration = (duration: any) => {
    console.log('Duration: ', duration);
    if (timeStartReset !== timeStart) {
      setTimeStart(timeStartReset);
    }
  }

  const binarySearch = (arr: number[][], searchIndex: number, x: number, start: number, end: number): any => {
    // Base Condition
    if (start > end) return false;

    // Find the middle index
    let mid = Math.floor((start + end) / 2);

    // Compare mid with given key x
    if (arr[mid][searchIndex] <= x && x < arr[mid + 1][searchIndex]) return mid;

    // If element at mid is greater than x,
    // search in the left half of mid
    if (arr[mid][searchIndex] > x)
      return binarySearch(arr, searchIndex, x, start, mid - 1);
    else
      // If element at mid is smaller than x,
      // search in the right half of mid
      return binarySearch(arr, searchIndex, x, mid + 1, end);
  };

  const getStationTime = async (stationIndex: number, timeSeconds: number): Promise<number | null> => {
    if (frameTimestamps === null) return null;

    let stationTimeSeconds = null;
    try {
      const frameIndex = binarySearch(frameTimestamps[stationIndex], 0, Math.floor(timeSeconds * 1000), 0, frameTimestamps[stationIndex].length - 1);
      stationTimeSeconds = frameTimestamps[stationIndex][frameIndex][1];
    } catch (error) {
      console.log('Error getting station time: ', error);
    }

    return stationTimeSeconds;
  }

  const convertStationTime = (fromIndex: number, fromTimeSeconds: number, toIndex: number): number => {
    if (frameTimestamps === null) return fromTimeSeconds;

    let toTimeSeconds = fromTimeSeconds;
    try {
      const fromFrameIndex = binarySearch(frameTimestamps[fromIndex], 0, Math.floor(fromTimeSeconds * 1000), 0, frameTimestamps[fromIndex].length - 1);
      const fromTimestamp = frameTimestamps[fromIndex][fromFrameIndex][1];
      const toFrameIndex = binarySearch(frameTimestamps[toIndex], 1, fromTimestamp, 0, frameTimestamps[toIndex].length - 1);
      toTimeSeconds = frameTimestamps[toIndex][toFrameIndex][0] / 1000;
    } catch (error) {
      console.log('Error converting station time: ', error);
    }

    return toTimeSeconds;
  }

  const onStationClick = (index: number) => {
    setStationIndex(index);
    setTimeStartReset(convertStationTime(stationIndex, timeStartStore, index));
  }

  const getStationBackgroundColor = (stationIndex: number, index: number, currenStationId: string) => {
    if (stationIndex === index) {
      return { backgroundColor: '#82b1fa' };
    } else {
      return { backgroundColor: '' };
    }
  }

  return (
    <Overlay
      onClose={handleClose}
      show
      renderFooter={() => <OrderOverlayFooter order={order} />}
    >
      <StyledContainer>
        {order.stationVideos[stationIndex].state === 'READY'
          ? <VideoPlayer
          key={order.stationVideos[stationIndex].videoUrl + (frameTimestamps ? ('-' + frameTimestamps[stationIndex][0][1])  : '-framesloading')}
            url={order.stationVideos[stationIndex].videoUrl}
            width="100%"
            height="auto"
            onPlay={handlePlay}
            onPause={handlePause}
            onProgress={handleProgress}
            onDuration={handleDuration}
            isPlaying={playing}
            skipToMarkers={true}
            controls={controls}
            timeStart={timeStart}
            volume={0}
            markers={markers}
          />
          : <img src={(order.stationVideos[stationIndex].state === 'PROCESSING')
            ? "/video-processing.svg"
            : "/video-not-found.svg"}
            width="100%" alt="video placeholder" />}
          <div className="stations">
            {order.stationVideos.length > 1 ? (
              <div>
              station: {order.stationVideos.map((video, index) => {
                return (<button style={getStationBackgroundColor(stationIndex, index, order.stationVideos[index].stationId)}
                  onClick={() => onStationClick(index)}
                  key={index}
                  title={order.stationVideos[index].stationId}>
                  {order.stationVideos[index].stationName}
                </button>)
              })}
              </div>
            ) : ''}
            <div style={{
              float: 'right'
            }}>
              <Clipboard style={{
                background: clipboardBgColor,
                border: 'none',
                cursor: 'pointer',
                transform: `scale(${clipboardSize})`
              }}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.origin + "/orders/" + order.id
                    + "/station/" + order.stationVideos[stationIndex].stationId
                    + "/vt/" + timeStartStore);
                  setClipboardBgColor("#f6e19b");
                  setTimeout(() => {
                    setClipboardBgColor("transparent");
                  }, 100);
                  setClipboardSize(1.5);
                  setTimeout(() => {
                    setClipboardSize(1);
                  }, 100);
                }}
                button-title="Copy Order Link">🔗</Clipboard>
            </div>
          </div>
        <div className="order-meta">
          <p className="order-brand">{order.brand.name}</p>
          <p className="order-id">{order.platformId}</p>
          <p className="order-timestamp">{displayedTimestamp}
            {showTimeToOrderReset ? (
              <button
                className='reset-time-to-order'
                onClick={onResetTimeToOrder}
                title='Reset time to order'>↺</button>
            ) : ''}
          </p>
          <table className="people">
            <thead>
              <tr>
                <th>Customer</th>
                {/* TODO: Driver and Client columns */}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {order.customer.name}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {(order && order.items && order.items.length > 0) ?
          <table className="order-items">
            <thead>
              <tr>
                <th>
                  <h4>
                    {order.items.length} Product{order.items.length !== 1 && 's'}
                  </h4>
                </th>
                <th>Qty</th>
                {/* TODO: Rate and Comment columns */}
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={index}>
                  <td>{item.name} {item.modifiers.map(modifier =>
                    <ul className="order-item-modifiers">
                      {((modifier.quantity !== 1) ? modifier.quantity + 'x ' : '')
                        + ((modifier.name) ? modifier.name + ': ' : '')
                        + modifier.choice}
                    </ul>)}</td>
                  <td className="order-item-quantity">{item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          : null}

        {(order.notes) ? (<table className="notes">
          <thead>
            <tr>
              <th>
                <h4>
                  Notes
                </h4>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{order.notes}</td>
            </tr>
          </tbody>
        </table>) : ''}

        <div className="order-status-timestamps">
          <h4>Timestamps</h4>
          <table>
            <tbody>
              {order.phaseStatuses.map((status, i) => (
                <tr key={status.phase.name}>
                  <td>
                    <span
                      role="img"
                      aria-label=""
                      className="status-dot"
                      style={{
                        backgroundColor: COLORS[i],
                      }}
                    />
                  </td>
                  <td>{status.phase.name}</td>
                  <td>{computeStatusDurationMinutes(status)} min</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </StyledContainer>
    </Overlay >
  );
};

export const OrderOverlayFooter = (props: { order: Order }) => (
  <OrderOverlayFooterContainer>
    <OrderStatusBar order={props.order} />
    <p className="order-duration">
      {Math.floor(computeOrderDuration(props.order) / MINUTE_IN_MS)} min
    </p>
  </OrderOverlayFooterContainer>
);

export const StyledContainer = styled.div`
      font-family: Helvetica, sans-serif;
      color: #6e757c;

      .stations {
        margin: 0px 20px;
      padding: 5px 0px 5px;
      font-size: 12px;
  }

      video {
        outline: none;
  }

      h3 {
        color: #325882;
      font-size: 32px;
      font-weight: bold;
      margin: 0 0 20px 0;
  }

      h4 {
        font - size: 16px;
      font-weight: bold;
      color: #000;
      text-transform: uppercase;
  }

      .keyframe-seek-container {
        text - align: right;
  }

      .order-meta {
        position: relative;
      margin: 0px 20px;
      padding: 0 0 20px 0;
      border-bottom: 1px solid #eee;

      .order-brand {
        position: absolute;
      right: 20px;
      top: -10px;
      font-weight: bold;
      font-size: 12px;
    }

      .order-timestamp {
        position: absolute;
      right: 20px;
      top: 5px;
      color: #325882;
      font-size: 12px;
    }

      .order-id {
        padding-right: 160px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
  }

      .order-items {
        margin: 10px 20px;
      text-align: left;
      width: calc(100% - 40px);
      border-collapse: collapse;

        .order-item-modifiers {
          margin-top: 10px;
          padding-left: 20px;
          font-size: 13.5px;
          line-height: 8px;
        }

        .order-item-quantity {
          vertical-align: top;
        }

      th,
      td {
        padding - right: 0.5em;
    }

      thead {
        th {
        font - size: 12px;
      color: #6e757c;
      }
    }

      tbody {
        padding: 20px 0;

      tr {
        td {
          font - size: 14px;
          padding: 0.5em 0;
          color: #6e757c;
        }
      }
    }


      tfoot {
        tr {
        td {
        padding - top: 20px;
      border-top: 1px solid #eee;
        }
      }
    }
  }

      .notes {
        margin: 10px 20px;
      text-align: left;
      width: calc(100% - 40px);
      border-collapse: collapse;

      th,
      td {
        padding - right: 0.5em;
    }

      tbody {
        tr {
        td {
        padding - top: 5px;
        }
      }
    }
  }

  .order-status-timestamps {
        padding: 10px 20px;

    table {
        width: 100%;
      color: #6e757c;
      font-size: 14px;

      td {
        padding: 0.5em 0;
      }
    }

    .status-dot {
        display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 50%;
    }
  }

.myButton {
        box - shadow:inset 0px 1px 0px 0px #fce2c1;
      background:linear-gradient(to bottom, #ffc477 5%, #fb9e25 100%);
      background-color:#ffc477;
      border-radius:6px;
      border:1px solid #eeb44f;
      display:inline-block;
      cursor:pointer;
      color:#ffffff;
      font-family:Arial;
      font-size:15px;
      font-weight:bold;
      padding:6px 24px;
      text-decoration:none;
      text-shadow:0px 1px 0px #cc9f52;
}
.myButton:hover {
        background:linear-gradient(to bottom, #fb9e25 5%, #ffc477 100%);
      background-color:#fb9e25;
}
.myButton:active {
        position:relative;
      top:1px;
}

.reset-time-to-order {
  margin: 0px 0px 0px 5px;
}

      `;

export const OrderOverlayFooterContainer = styled.div`
      padding: 0 20px;
      position: relative;
      height: 100%;
      display: flex;
      justify-content: space-around;
      align-items: center;

      .order-duration {
        font - family: Helvetica, sans-serif;
      font-size: 16px;
      font-weight: bold;
      color: #000;
      text-transform: uppercase;
  }

      `;

export default OrderDetailsOverlay;
